import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ShieldCheck, ShieldAlert, Search, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";

export default function RiskCheck() {
  const [query, setQuery] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [highWarn, setHighWarn] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    if (!query.trim()) return toast.error("Enter mobile number or CNIC.");
    if (!consent) return toast.error("Please confirm consent to continue.");
    setLoading(true);
    try {
      const { data } = await api.post("/risk/check", { query: query.trim(), consent: true });
      setResult(data);
      if (data.risk_level === "high") setHighWarn(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check failed");
    } finally {
      setLoading(false);
    }
  };

  const riskTone = result
    ? result.risk_level === "high"
      ? "bg-red-50 border-red-300 text-red-700"
      : result.risk_level === "medium"
      ? "bg-amber-50 border-amber-300 text-amber-700"
      : "bg-emerald-50 border-emerald-300 text-emerald-700"
    : "";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="risk-check-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Risk Check</h1>
        <p className="text-slate-500 mt-1">
          Query the HisaabPro credit network for aggregated signals — no shop-specific data is shared.
        </p>
      </div>

      <form onSubmit={run} className="bg-white border border-slate-200 rounded-md p-6 space-y-5">
        <div className="space-y-2">
          <Label htmlFor="q" className="text-sm font-semibold">Mobile number or CNIC</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="q"
              placeholder="03xx-xxxxxxx or 35202-xxxxxxx-x"
              className="pl-9 tabular"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="risk-query-input"
            />
          </div>
          <p className="text-xs text-slate-500 inline-flex items-center gap-1">
            <Info className="w-3 h-3" /> Enter either the 11-digit mobile or 13-digit CNIC.
          </p>
        </div>

        <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 p-3">
          <Checkbox
            id="consent"
            checked={consent}
            onCheckedChange={setConsent}
            className="mt-0.5"
            data-testid="risk-consent-checkbox"
          />
          <Label htmlFor="consent" className="text-sm font-normal leading-relaxed text-slate-700">
            I confirm I have the customer's consent to check their credit risk on the HisaabPro network.
            Only aggregated signals will be returned — no shop-specific identifiers or personal data.
          </Label>
        </div>

        <Button
          type="submit"
          disabled={loading || !consent}
          className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
          data-testid="risk-run-btn"
        >
          {loading ? "Checking..." : "Check Customer Risk"}
        </Button>
      </form>

      {result && (
        <div className={`rounded-md border p-6 ${riskTone}`} data-testid="risk-result-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              {result.risk_level === "high" ? <ShieldAlert className="w-7 h-7" /> : <ShieldCheck className="w-7 h-7" />}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide">Risk level</div>
                <div className="font-display text-3xl font-bold capitalize">{result.risk_level}</div>
              </div>
            </div>
            <div className="flex gap-2">
              {result.defaulter && <Badge className="bg-red-600 hover:bg-red-600">Defaulter</Badge>}
              {!result.found && <Badge className="bg-slate-600 hover:bg-slate-600">No history</Badge>}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div>
              <div className="text-xs uppercase tracking-wide font-semibold opacity-70">Active loans</div>
              <div className="font-display text-2xl font-bold tabular mt-0.5">{result.active_loans}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide font-semibold opacity-70">Late payments</div>
              <div className="font-display text-2xl font-bold tabular mt-0.5">{result.late_payment_pct}%</div>
            </div>
            {result.max_days_late != null && (
              <div>
                <div className="text-xs uppercase tracking-wide font-semibold opacity-70">Max days late</div>
                <div className="font-display text-2xl font-bold tabular mt-0.5">{result.max_days_late}</div>
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wide font-semibold opacity-70">Defaulter</div>
              <div className="font-display text-2xl font-bold mt-0.5">{result.defaulter ? "Yes" : "No"}</div>
            </div>
          </div>

          <div className="mt-5 text-xs opacity-80 border-t border-current/20 pt-3">
            {result.message}
          </div>
        </div>
      )}

      <AlertDialog open={highWarn} onOpenChange={setHighWarn}>
        <AlertDialogContent data-testid="high-risk-dialog">
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-full bg-red-100 border border-red-200 flex items-center justify-center mx-auto mb-2">
              <ShieldAlert className="w-6 h-6 text-red-600" />
            </div>
            <AlertDialogTitle className="text-center">High-risk customer detected</AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-2">
              <span className="block">
                The HisaabPro network flagged this person as <span className="font-bold text-red-600">HIGH</span> risk.
              </span>
              {result && (
                <span className="block rounded-md bg-red-50 border border-red-200 p-3 text-sm text-slate-700 tabular">
                  {result.defaulter && "⚠ Marked as defaulter. "}
                  {result.late_payment_pct}% late payments · {result.active_loans} active loan(s)
                </span>
              )}
              <span className="block text-xs text-slate-500 pt-1">
                Consider asking for a larger down-payment, shorter tenure or a guarantor.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 w-full" data-testid="high-risk-ack-btn">
              Acknowledge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
